/**
 * 폼 입력 사전 검증.
 *
 * 정본 검증은 서버입니다 — 여기는 "눌러 보고 서버 오류를 읽는" 왕복을 줄이는 UX 용입니다. 규칙은
 * 서버와 **같은 식**이어야 합니다: 여기서만 느슨하면 서버 400 이 나고, 여기서만 엄격하면 서버가
 * 받는 값을 콘솔이 막습니다.
 *
 * 각 함수는 통과면 `null`, 아니면 사용자에게 보일 문구를 돌려줍니다.
 */

import { isHttpsURL } from "./format";

/** 서버 `domain.ValidateSiteCode`(`siteCodeRe`) 와 같은 식입니다. */
const SITE_CODE_RE = /^[a-z0-9][a-z0-9_-]{0,19}$/;
const SITE_CODE_HINT = "소문자·숫자로 시작하고 소문자·숫자·-·_ 만 쓸 수 있습니다(최대 20자).";

/** 전역 네임스페이스 예약값(서버 `ReservedOwningSystem`). */
const RESERVED_OWNING_SYSTEM = "plt";

export function validateSiteCode(raw: string): string | null {
  if (raw === "") return "서비스 코드를 입력하세요.";
  return SITE_CODE_RE.test(raw) ? null : `서비스 코드: ${SITE_CODE_HINT}`;
}

/** 소속 시스템은 선택값입니다 — 비어 있으면 통과. 값이 있으면 사이트 코드 형식이어야 합니다. */
export function validateOwningSystem(raw: string): string | null {
  if (raw === "") return null;
  if (raw === RESERVED_OWNING_SYSTEM) return "plt 는 예약된 값이라 소속 시스템으로 쓸 수 없습니다.";
  return SITE_CODE_RE.test(raw) ? null : `소속 시스템: ${SITE_CODE_HINT}`;
}

/** 서버 `domain.ValidateSiteEntryURL` — https 절대 URL 만 받습니다. */
export function validateEntryURL(raw: string): string | null {
  return isHttpsURL(raw) ? null : "진입 주소는 https:// 로 시작하는 전체 주소여야 합니다.";
}

/** 서버 `domain.SupportedScopes`. openid 는 OIDC 클라이언트면 항상 포함합니다. */
export const OIDC_OPTIONAL_SCOPES = ["email", "profile", "groups"] as const;

/** 서버 `domain.ValidateOIDCRedirectURI` — https(loopback 만 http), fragment 금지, 최대 10개. */
export function validateRedirectURIs(uris: string[]): string | null {
  if (uris.length > 10) return "돌려보낼 주소는 최대 10개입니다.";
  const bad = uris.find((raw) => !isRedirectURI(raw));
  return bad ? `돌려보낼 주소가 올바르지 않습니다: ${bad}` : null;
}

function isRedirectURI(raw: string): boolean {
  if (!URL.canParse(raw) || raw.includes("#")) return false;
  const u = new URL(raw);
  return u.protocol === "https:" || (u.protocol === "http:" && ["localhost", "127.0.0.1"].includes(u.hostname));
}

/** 빈 값이면 `message` — 문구는 호출부가 칸 이름에 맞게 씁니다(조사를 기계적으로 붙이지 않기 위해). */
export const required = (raw: string, message: string): string | null =>
  raw === "" ? message : null;

/** 첫 번째 실패 문구 — 폼은 한 번에 한 문제만 보여줍니다(위에서부터 고치게). */
export const firstError = (...results: (string | null)[]): string | null =>
  results.find((r) => r !== null) ?? null;
